import { useMemo, useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { Layout, TextLink } from 'components'
import { CurrentUserContextProvider } from 'context'
import { Text, Modal, Skeleton, Title, Grid, Badge, Tooltip, Card, ScrollArea, Table } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { IconChevronLeft, IconEye } from '@tabler/icons-react'
import { useAnalyticalReport, useStudentDetails } from './hooks/useAnalyticalReport'
import { StudentData } from './service/types/interfaces'
import StudentDetailCard from './components/StudentDetailCard'

export function AnalyticalReport() {
  const { groupId } = useParams<{ groupId: string }>()
  const { data, loadingData } = useAnalyticalReport()
  const { studentDetails, loadingDetails, fetchStudentDetails } = useStudentDetails(groupId)
  const { t } = useTranslation()
  const [selectedStudent, setSelectedStudent] = useState<StudentData | null>(null)
  const location = useLocation()
  const navigate = useNavigate()

  const queryParams = new URLSearchParams(location.search)
  const groupName = queryParams.get('groupName')

  const columns = useMemo(() => {
    if (data.length === 0) return []

    const filteredData = data.map(student => ({
      ...student,
      items: student.items.filter(item => {
        const key = Object.keys(item)[0]
        return item[key] !== null
      }),
    }))

    const itemKeys = filteredData[0].items.map(item => Object.keys(item)[0])
    const itemHeaders = filteredData[0].items.map(item => item[Object.keys(item)[0]].lv1Item)

    return [
      {
        accessorKey: 'studentName',
        header: t('studentName').slice(0, 15),
        Cell: ({ row }: { row: { original: StudentData } }) => (
          <Text style={{ cursor: 'default', fontWeight: 'bold' }}>{row.original.studentName}</Text>
        ),
      },
      ...itemKeys.map((key, index) => ({
        accessorKey: `items[${index}].${key}`,
        header: (
          <Tooltip label={itemHeaders[index]} withArrow position='top'>
            <Text style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {itemHeaders[index]?.slice(0, 15)}
            </Text>
          </Tooltip>
        ),
        Cell: ({ row }: { row: { original: StudentData } }) => {
          const item = row.original.items.find(item => item[key])
          const handleClick = () => {
            const lv1ItemId = key.replace('lv1Item', '')
            setSelectedStudent({
              ...row.original,
              lv1ItemId,
            })
            fetchStudentDetails(row.original.studentId, lv1ItemId)
          }

          const average = item?.[key]?.average || 0
          const percentage = item?.[key]?.percentage || 0
          let color = 'green'
          if (average <= 2) color = 'red'
          else if (average === 3) color = 'blue'

          return (
            <Tooltip label={t('show_details')}>
              <Text onClick={handleClick} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <Badge color={color} style={{ marginRight: 4 }}>
                  {average}
                </Badge>
                {` | ${percentage}%`}
                <IconEye size='15px' style={{ marginLeft: 4 }} />
              </Text>
            </Tooltip>
          )
        },
      })),
    ]
  }, [data, t, fetchStudentDetails])

  return (
    <CurrentUserContextProvider>
      <Layout>
        <TextLink size='md' iconLeft={<IconChevronLeft />} onClick={() => navigate(-1)}>
          {t('back')}
        </TextLink>
        <Title order={3} mt='md'>
          {groupName}
        </Title>
        <Card shadow='sm' padding='lg' mt='lg'>
          {loadingData ? (
            <Skeleton height={400} />
          ) : data.length > 0 ? (
            <ScrollArea>
              <Table>
                <thead>
                  <tr>
                    {columns.map((column, index) => (
                      <th key={index}>{column.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((student, rowIndex) => (
                    <tr key={rowIndex}>
                      {columns.map((column, colIndex) => (
                        <td key={colIndex}>
                          {column.Cell ? column.Cell({ row: { original: student } }) : (student as any)[column.accessorKey]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ScrollArea>
          ) : (
            <Text>{t('noDataFound')}</Text>
          )}
        </Card>

        <Modal
          opened={!!selectedStudent}
          onClose={() => {
            setSelectedStudent(null)
          }}
          title={t('detailsTitle')}
          size='lg'
        >
          <Text weight={500} size='lg'>
            {selectedStudent?.studentName}
          </Text>
          {loadingDetails ? (
            <Skeleton height={200} />
          ) : studentDetails && studentDetails.length > 0 ? (
            <Grid>
              {studentDetails.map((detail, index) => (
                <StudentDetailCard detail={detail} key={index} />
              ))}
            </Grid>
          ) : (
            <Text>{t('noDetailsFound')}</Text>
          )}
        </Modal>
      </Layout>
    </CurrentUserContextProvider>
  )
}

export default AnalyticalReport
